/**
 * App entry point. Mounts <App /> under StrictMode + the TanStack Query
 * client, wrapped in Sentry.ErrorBoundary so React render-tree exceptions
 * are reported instead of producing a white screen.
 *
 * Sentry init runs at module load (before render) so any throw during
 * the initial bootstrap is captured. No-ops when VITE_SENTRY_DSN is
 * unset (i.e. local dev), so safe to always run.
 */

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";

// Inter Variable, self-hosted via @fontsource — no Google Fonts CDN
// dependency. wght axis 100..900 supports every weight we use without
// loading multiple static files.
// @ts-expect-error: no types from @fontsource-variable/inter
import "@fontsource-variable/inter";

import App from "@/App";
import "./index.css";

// Sentry: errors-only, no performance traces, no session replay.
// See backend/app/main.py for the matching server-side init + the project
// CLAUDE.md "Sentry" section for the rationale.
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE, // "production" | "development"
    release: import.meta.env.VITE_SENTRY_RELEASE,
    // No tracing / replay / profiling — matches the v1 scope in the plan.
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    // We attach user via Sentry.setUser() in AuthProvider; don't auto-collect.
    sendDefaultPii: false,
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Match iOS behavior: refetch when user returns to tab. Helps the
      // Skills / Leak Report views update without manual reload after
      // running drills in another tab.
      refetchOnWindowFocus: true,
      staleTime: 30_000,
      retry: 1,
    },
  },
});

// Minimal fallback for any unhandled render-tree exception. Pretty-enough
// for v1; the "we've been notified" line is honest only if Sentry is
// actually configured (no-op in dev — but dev developers see the error
// in console anyway).
function ErrorFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-bg text-text px-4">
      <div className="max-w-md text-center space-y-3">
        <h1 className="text-xl font-semibold">Something went wrong</h1>
        <p className="text-sm text-text-muted">
          We've been notified and will look into it. Reloading usually helps.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-2 px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-accent/10 hover:border-accent transition"
        >
          Reload
        </button>
      </div>
    </div>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
